import { CalculationContext, FieldKey, SetOptions } from '@marketiq/calc-core';
import { ColumnIdMap } from './column-id-map';

/** Minimal shape of a catalog Column the rule-engine passes (avoids a hard @miq/catalog dependency). */
export interface CatalogColumnLike {
  columnId: number;
  value: unknown;
  unit?: { value: unknown } | null;
  options?: { format?: string } | null;
}

/** Minimal shape of a catalog Section (a bag of columns). */
export interface CatalogSectionLike {
  columns: CatalogColumnLike[];
}

/**
 * Bridges the catalog **rule-engine** path (Column/Section data, keyed by numeric `columnId`) to the
 * calc-core {@link CalculationContext}. The non-reactive sibling of `FormGroupCalculationContext`:
 * it lets `sections-override` calc-functions and any Column-shaped consumer drive the same calc-core
 * rules, then hand back the columns that changed (what those functions return).
 *
 *   const ctx = new ColumnSectionCalculationContext(section, columnIdMap);
 *   runRulesForInput(ctx, changedKey, leaseRules, { round });
 *   return ctx.modifiedColumns();
 */
export class ColumnSectionCalculationContext implements CalculationContext {
  private readonly modified = new Map<number, CatalogColumnLike>();

  constructor(
    private readonly section: CatalogSectionLike,
    private readonly columnIdMap: ColumnIdMap
  ) {}

  get<T = unknown>(key: FieldKey): T | null | undefined {
    return this.column(key)?.value as T | null | undefined;
  }

  set<T = unknown>(key: FieldKey, value: T, _opts?: SetOptions): void {
    const col = this.column(key);
    if (col) {
      col.value = value;
      this.modified.set(col.columnId, col);
    }
  }

  has(key: FieldKey): boolean {
    return !!this.column(key);
  }

  /** Columns whose value was written during the rule run — the rule-engine's expected return value. */
  modifiedColumns(): CatalogColumnLike[] {
    return [...this.modified.values()];
  }

  private column(key: FieldKey): CatalogColumnLike | undefined {
    const id = this.columnIdMap.columnIdFor?.(key);
    return id == null ? undefined : this.section.columns.find(column => column.columnId === id);
  }
}
